import * as React from "react";
import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { FilterAltOffOutlined, Search } from "@mui/icons-material";
import { usePageFetch } from "../../hooks";

type FilterPanelProps = {
  initialFilters: TrainStepSearchRequest;
  onChange: (filters: TrainStepSearchRequest) => void;
};

export const FilterPanel = ({ initialFilters, onChange }: FilterPanelProps) => {
  const [query, setQuery] = useState("");
  const [from, setFrom] = React.useState<Dayjs | null>(null);
  const [to, setTo] = React.useState<Dayjs | null>(null);
  const [notReady, setNotReady] = useState(false);
  const [marketId, setMarketId] = useState<number | null>(null);
  const { data: markets } = usePageFetch<Market[]>("/api/home/markets");

  useEffect(() => {
    setQuery(initialFilters.query);
    setFrom(initialFilters.from ?? null);
    setTo(initialFilters.to ?? null);
    setNotReady(initialFilters.notReady);
    setMarketId(initialFilters.marketId);
  }, [initialFilters]);

  const hasFilters =
    query !== "" ||
    from !== null ||
    to !== null ||
    notReady ||
    marketId !== null;

  const resetFilters = () => {
    setQuery("");
    setFrom(null);
    setTo(null);
    setNotReady(false);
    setMarketId(null);
    onChange({
      query: "",
      from: null,
      to: null,
      notReady: false,
      marketId: null,
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onChange({
          query,
          from: from?.isValid() ? from : null,
          to: to?.isValid() ? to : null,
          notReady,
          marketId,
        });
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: (theme) => theme.spacing(3),
          gap: (theme) => theme.spacing(3),
        }}
      >
        <TextField
          variant={"standard"}
          name="query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          label="Nom de l'étape ou du sillon"
        />
        <DatePicker
          label="Depuis le"
          value={from}
          onChange={(val) => setFrom(val)}
          renderInput={(params) => (
            <TextField {...params} variant="standard" name="from" />
          )}
        />
        <DatePicker
          label="Jusqu'au"
          value={to}
          onChange={(val) => setTo(val)}
          renderInput={(params) => (
            <TextField {...params} variant="standard" name="to" />
          )}
        />
        <TextField
          select
          variant="standard"
          name="marketId"
          label="Marché"
          value={marketId ?? ""}
          onChange={(e) =>
            setMarketId(e.target.value === "" ? null : Number(e.target.value))
          }
        >
          {/* Keep the selected market even if it isn't in the list (yet) */}
          {marketId !== null &&
            markets &&
            !markets.some((m) => m.id === marketId) && (
              <MenuItem value={marketId}>#{marketId}</MenuItem>
            )}
          {markets?.map((market) => (
            <MenuItem key={market.id} value={market.id}>
              {market.name} ({market.axe})
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              name="notReady"
              checked={notReady}
              onChange={(e) => setNotReady(e.target.checked)}
            />
          }
          label="Avec au moins un message « train non prêt »"
        />
        <Box
          sx={{
            display: "flex",
            gap: (theme) => theme.spacing(1),
          }}
        >
          {hasFilters && (
            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{ flex: 1, gap: (theme) => theme.spacing(1) }}
            >
              Réinitialiser
              <FilterAltOffOutlined />
            </Button>
          )}
          <Button
            variant="contained"
            type="submit"
            sx={{ flex: 1, gap: (theme) => theme.spacing(1) }}
          >
            Rechercher
            <Search />
          </Button>
        </Box>
      </Box>
    </form>
  );
};
